import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
    listOrgMembers,
    loadMoreOrgMembers
} from "../Actions/API/Org/OrgMemberActions"
import { APIOrgMember } from "../Models/API/APIOrgMember"
import { FetchState } from "../Models/Client/FetchState"
import { getItemList, getPageToFetch } from "../Models/Client/RemoteData"
import { SortExpression } from "../Models/Client/SortExpression"
import { Result } from "../Models/Utility/Result"
import { MainThunkDispatch } from "../Reducers"
import { useAppState } from "./useReduxState"

export function useOrgMemberList(
    orgUsername: string,
    sort: SortExpression<APIOrgMember>
): [
    FetchState<{
        items: APIOrgMember[]
        count: number
        totalCount: number
    }>,
    () => Promise<void>
] {
    const dispatch = useDispatch<MainThunkDispatch>()
    const remoteData = useAppState(state => state.api.orgs.members[orgUsername])
    const handleLoadMore = useCallback(
        () => dispatch(loadMoreOrgMembers(orgUsername)),
        [dispatch, orgUsername]
    )

    useEffect(() => {
        const page =
            remoteData === undefined ? 0 : getPageToFetch(remoteData, sort)

        if (
            page >= 0 ||
            JSON.stringify(sort) !== JSON.stringify(remoteData?.list?.params)
        ) {
            dispatch(listOrgMembers(orgUsername, page, sort))
        }
    }, [dispatch, orgUsername, remoteData, sort])

    const fetchState = useMemo(() => {
        if (remoteData === undefined) {
            return undefined
        }

        return Result.ok({
            items: getItemList(remoteData),
            count: remoteData.list?.total ?? 0,
            totalCount: remoteData.list?.total ?? 0
        })
    }, [remoteData])

    return [fetchState, handleLoadMore]
}
